import React, { useState, useEffect } from 'react';
import { Elements, PaymentElement, useStripe, useElements } from '@stripe/react-stripe-js';
import { getStripe } from '../../lib/stripe';
import { getSupabaseClient } from '../../lib/supabase';

interface AddPaymentMethodModalProps {
  isOpen: boolean;
  onClose: () => void;
  onAdded: () => void;
}

const PaymentForm: React.FC<{
  onSuccess: () => void;
  onError: (error: string) => void;
  onCancel: () => void;
}> = ({ onSuccess, onError, onCancel }) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      return;
    }

    setProcessing(true);

    try {
      const { error } = await stripe.confirmSetup({
        elements,
        redirect: 'if_required',
        confirmParams: {
          return_url: window.location.href,
        },
      });

      if (error) {
        onError(error.message || 'Failed to add payment method');
      } else {
        onSuccess();
      }
    } catch (err) {
      onError(err instanceof Error ? err.message : 'Failed to add payment method');
    } finally {
      setProcessing(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      <div className="mb-6">
        <div className="rounded-lg border border-[#2C2731]/30 bg-[#FFFCEF]/60 px-3 py-4 shadow-inner">
          <PaymentElement options={{ layout: 'tabs' }} />
        </div>
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          onClick={onCancel}
          className="rounded-lg border border-[#2C2731] px-4 py-2 text-sm font-semibold text-[#2C2731] transition hover:bg-[#E8DFBD]"
          disabled={processing}
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={!stripe || processing}
          className="rounded-lg bg-[#2C2731] px-4 py-2 text-sm font-semibold text-[#FFFCEF] transition hover:bg-[#1F1924] disabled:cursor-not-allowed disabled:opacity-50"
        >
          {processing ? 'Processing...' : 'Save Card'}
        </button>
      </div>
    </form>
  );
};

export const AddPaymentMethodModal: React.FC<AddPaymentMethodModalProps> = ({
  isOpen,
  onClose,
  onAdded,
}) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      loadSetupIntent();
    }
  }, [isOpen]);

  const loadSetupIntent = async () => {
    setLoading(true);
    setError(null);

    try {
      const supabase = getSupabaseClient();

      // Ensure customer exists
      const { error: customerError } = await supabase.functions.invoke('stripe-ensure-customer', {
        method: 'POST',
      });

      if (customerError) {
        throw customerError;
      }

      // Create setup intent
      const { data: setupData, error: setupError } = await supabase.functions.invoke<{
        clientSecret: string;
      }>('stripe-create-setup-intent', {
        method: 'POST',
      });

      if (setupError) {
        throw setupError;
      }

      if (!setupData?.clientSecret) {
        throw new Error('Failed to create setup intent');
      }

      setClientSecret(setupData.clientSecret);
    } catch (err) {
      console.error('Failed to load setup intent:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize payment form');
    } finally {
      setLoading(false);
    }
  };

  const handleSuccess = () => {
    onAdded();
    onClose();
  };

  const handleError = (errorMessage: string) => {
    setError(errorMessage);
  };

  if (!isOpen) {
    return null;
  }

  return (
    <>
      {/* Overlay */}
      <div
        className="fixed inset-0 z-40 bg-black/50"
        onClick={onClose}
      />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto p-4">
        <div className="w-full max-w-md max-h-[90vh] overflow-y-auto rounded-2xl border border-[#2C2731] bg-[#FFFCEF] p-6 shadow-xl shadow-[#2C2731]/20">
          <div className="mb-4 flex items-center justify-between">
            <h2 className="font-recoleta text-xl font-semibold text-[#2C2731]">Add Payment Method</h2>
            <button
              onClick={onClose}
              className="rounded-full p-1 text-[#2C2731]/50 transition hover:bg-[#E8DFBD] hover:text-[#2C2731]"
              disabled={loading}
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {error && (
            <div className="mb-4 rounded-lg border border-red-300 bg-[#FBE2E2] px-3 py-2 text-sm text-red-700">
              {error}
            </div>
          )}

          {loading ? (
            <div className="flex items-center justify-center py-8">
              <div className="h-8 w-8 animate-spin rounded-full border-b-2 border-[#2C2731]" />
            </div>
          ) : clientSecret ? (
            <Elements
              stripe={getStripe()}
              options={{
                clientSecret,
              appearance: {
                theme: 'stripe',
                variables: {
                  colorPrimary: '#2C2731',
                  colorBackground: '#FFFCEF',
                  colorText: '#2C2731',
                },
              },
            }}
          >
              <PaymentForm onSuccess={handleSuccess} onError={handleError} onCancel={onClose} />
            </Elements>
          ) : null}
        </div>
      </div>
    </>
  );
};
